import React from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
// components
import Avatar from 'components/Avatar/Avatar';
import FarmTypeLabel from 'components/FarmTypeLabel';
import { GroupStatusBadge } from 'components/StatusBadge/StatusBadge';
import ResponsiveDropdown from 'components/ResponsiveDropdown';
import Preloader from 'components/Preloader';
import PigGroupMarketingBadge from 'components/PigGroupMarketingBadge';
import TextLabel from '../../TextLabel/TextLabel';
import Link from 'react-router/lib/Link';
import { FormattedMessage } from 'react-intl';
import { ThreeDotsBtn } from 'components/Button/Button';
import PlusMoreHint from 'components/PlusMoreHint';
import ABFBadge from 'components/ABFBadge';
import ProfilePlaceholder from '../ProfilePlaceholder';
// utils
import cn from 'classnames';
import isEmpty from 'lodash.isempty';
// regional date handling
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUserShort } from 'utils/regionalDateHelper';
// styles
import './GroupProfileHeader.scss';

const GroupProfileHeader = ({
  children,
  className,
  countryCode,
  dropdownOptions = [],
  group = {},
  isLoading,
  isSticky,
  onEditName,
  showHealthStatus = false,
}) => {
  const groupFarm = group?.farm || {};
  const externalSource = group?.external_source || {};
  const textValues = { date: null };

  return (
    <div className={cn('GroupProfileHeader', { isSticky, [className]: !!className })}>
      <Preloader isActive={isLoading} />

      <ProfilePlaceholder isActive={isLoading && !group?.id}>
        <Avatar type="pig_group" avatarSize={50} iconSize={24} />

        <div className="group-information">
          <div className="group-info-wrapper">
            {group?.id ? (
              <Link
                to={`/groups/${group.id}/flow`}
                target="_blank"
                rel="noopener noreferrer"
                className="group-name primary-link"
              >
                {group?.name}
              </Link>
            ) : (
              <span className="group-name">{group?.name}</span>
            )}
            {onEditName &&
              <i className="fa fa-pencil-mdc" onClick={onEditName} />
            }
            <PigGroupMarketingBadge className="mr-5" pigGroup={group} isHintAttachedToBody />
            <GroupStatusBadge status={group?.status} textValues={textValues} />
            {group.abf_tracking && <ABFBadge className="ml-5" />}
          </div>
          <div className="group-farm-info">
            <FormattedMessage id="general.farm" /> •&nbsp;
            <Link className="primary-link" to={`/barnsheets/farms/${groupFarm.id}/groups`}>{groupFarm.name}</Link>&nbsp;
            ({groupFarm.external_link_id || 'N/A'}) <FarmTypeLabel farmType={groupFarm.farm_type} />
            {' '}
            {group.barns && (
              <PlusMoreHint className="barns-line" items={group.barns}>
                <FormattedMessage id="general.barns" />
                {' • '}
              </PlusMoreHint>
            )}
            {!isEmpty(externalSource) && (
              <div>
                <FormattedMessage id="general.source" /> •&nbsp;
                <Link className="primary-link" to={`/sources/${externalSource.id}`}>
                  {externalSource.name || ''}
                </Link>&nbsp;
                <TextLabel className="fs-11">{externalSource.source_type}</TextLabel>
              </div>
            )}
            {showHealthStatus && !!group?.pig_group_diagnoses_names?.length && (
              <div>
                <FormattedMessage id="general.healthStatus" /> •&nbsp;
                <span className="semibold">
                  {group?.pig_group_diagnoses_info?.length
                    ? group.pig_group_diagnoses_info.map((info, index) => (
                      <span key={`diagnosis-info-${info.id}`}>
                        {index > 0 && ', '}
                        {info.source_farm_name ? (
                          <FormattedMessage
                            id="general.diagnosisWithProvenance"
                            values={{
                              name: info.name,
                              farm: info.source_farm_name,
                              date: formatDateForUserShort(info.created_at, countryCode),
                            }}
                          />
                        ) : info.name}
                      </span>
                    ))
                    : group.pig_group_diagnoses_names.join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
      </ProfilePlaceholder>

      <div className="group-actions">
        {children}
        {!!dropdownOptions.length && (
          <ResponsiveDropdown
            options={dropdownOptions}
            className="group-profile-dropdown"
            trigger={<ThreeDotsBtn />}
          />
        )}
      </div>
    </div>
  );
};

GroupProfileHeader.propTypes = {
  group: T.object,
  className: T.string,
  countryCode: T.string,
  isSticky: T.bool,
  children: T.any,
  isLoading: T.bool,
  dropdownOptions: T.array,
  onEditName: T.func,
  showHealthStatus: T.bool,
};

export default connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  })
)(GroupProfileHeader);
